import type { UserInfo, GroupInfo } from './types';
import { getDeviceID, getClientIP, encodeSand } from '@/utils/auth';
import { API_PERMS_SMS_CODE, API_PERMS_EMAIL_CODE, API_PERMS_USER, API_PERMS_LOGOUT } from '@/constants';
import request from '@/utils/request';

// ─── 验证码 ───────────────────────────────────────────────────────────────────

/**
 * 获取手机短信验证码
 * @returns context_token，提交登录时需携带
 */
export async function getSmsCode(phone: string, authType: 1 | 2 = 1): Promise<string> {
  const deviceId = getDeviceID();
  const timestamp = Date.now();
  const encryptedPhone = encodeSand(deviceId, phone, String(timestamp)).auth;

  const data = await request.post<string | number>(API_PERMS_SMS_CODE, {
    auth_type: authType,
    timestamp,
    mobile: encryptedPhone,
    device_id: deviceId,
    client_ip: await getClientIP(),
  });
  return String(data);
}

/**
 * 获取邮箱验证码
 * @returns context_token，提交登录时需携带
 */
export async function getEmailCode(email: string): Promise<string> {
  const deviceId = getDeviceID();
  const timestamp = Date.now();
  const encryptedEmail = encodeSand(deviceId, email, String(timestamp)).auth;

  const data = await request.post<string | number>(API_PERMS_EMAIL_CODE, {
    auth_type: 5,
    timestamp,
    email: encryptedEmail,
    device_id: deviceId,
    client_ip: await getClientIP(),
  });
  return String(data);
}

// ─── 用户信息 ─────────────────────────────────────────────────────────────────

/**
 * 获取当前登录用户信息（POST /api/perms/user）
 * 未登录时后端返回 401 或 code !== 0，此处抛出错误由调用方处理。
 */
export async function fetchUserInfo(): Promise<UserInfo> {
  const d = await request.post<any>(API_PERMS_USER, {});

  // API 返回 snake_case，映射为 camelCase
  const groupMap: Record<string, GroupInfo> = {};
  if (d.group_map) {
    for (const [key, g] of Object.entries(d.group_map as Record<string, any>)) {
      groupMap[key] = {
        groupId: (g as any).group_id,
        groupName: (g as any).group_name,
        groupLogo: (g as any).group_logo,
        description: (g as any).description,
        productList: (g as any).product_list ?? [],
      };
    }
  }
  return {
    userId: d.user_id,
    userName: d.user_name,
    account: d.account ?? '',
    avatar: d.avatar ?? '',
    email: d.email ?? '',
    mobile: d.mobile ?? '',
    platform: d.platform ?? '',
    perms: d.perms ?? [],
    abilities: d.abilities ?? [],
    roles: d.roles ?? [],
    pagePaths: d.page_paths ?? [],
    apiPaths: d.api_paths ?? [],
    abilitieMap: d.abilitie_map ?? {},
    roleMap: d.role_map ?? {},
    groupMap,
    groupMapTeams: d.group_map_teams,
  };
}

// ─── 登出 ─────────────────────────────────────────────────────────────────────

/**
 * 调用 POST /api/perms/logout 清空后端 session，然后跳转登录页。
 * 接口异常时仍强制跳转，确保前端状态清空。
 */
export async function logout(): Promise<void> {
  // 清空所有本地缓存
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auto_seat_id');
    localStorage.removeItem('tos_selected_team');
    localStorage.removeItem('tos_current_status');
    localStorage.removeItem('tos_current_car');
  }

  try {
    await request.get(API_PERMS_LOGOUT, {});
  } catch {
    // 接口异常不阻断流程，继续跳登录页
  }
  window.location.replace('/login');
}
